import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { Bug, Crosshair, ShieldCheck, Terminal } from 'lucide-react';

export function ArchDefender() {
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const [bugs, setBugs] = useState<{ id: number; x: number; y: number; type: string }[]>([]);
    const [gameState, setGameState] = useState<'idle' | 'active' | 'over'>('idle');
    const [timeLeft, setTimeLeft] = useState(30);
    const containerRef = useRef<HTMLDivElement>(null);
    const nextId = useRef(0);

    const spawnBug = () => {
        if (gameState !== 'active') return;
        const x = Math.random() * 80 + 10; // 10% to 90%
        const y = Math.random() * 80 + 10;
        const types = ['LOG_ERROR', 'MEMORY_LEAK', 'ZERO_DAY', 'TRACE_FAULT'];
        const type = types[Math.floor(Math.random() * types.length)];

        const newBug = { id: nextId.current++, x, y, type };
        setBugs(prev => [...prev, newBug]);

        // Auto remove if not clicked
        setTimeout(() => {
            setBugs(prev => prev.filter(b => b.id !== newBug.id));
        }, 1500);
    };

    useEffect(() => {
        let timer: any;
        if (gameState === 'active') {
            timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        setGameState('over');
                        return 0;
                    }
                    return prev - 1;
                });
                if (Math.random() > 0.3) spawnBug();
            }, 800);
        }
        return () => clearInterval(timer);
    }, [gameState]);

    const handleCatch = (id: number) => {
        setScore(prev => prev + 100);
        setBugs(prev => prev.filter(b => b.id !== id));
    };

    const startGame = () => {
        setScore(0);
        setTimeLeft(30);
        setBugs([]);
        setGameState('active');
        if (score > highScore) setHighScore(score);
    };

    return (
        <GlassCard className="p-8 w-full max-w-2xl border-emerald-500/20 bg-black/60 relative overflow-hidden h-[500px] flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <Terminal className="text-emerald-500 w-5 h-5" />
                    <div>
                        <h3 className="text-xs font-mono text-emerald-500 uppercase tracking-[0.2em]">Arch Defender</h3>
                        <p className="text-[10px] text-gray-500 font-mono">PURGING_UNSTABLE_NODES...</p>
                    </div>
                </div>
                <div className="flex gap-8 font-mono">
                    <div className="flex flex-col items-end">
                        <span className="text-[9px] text-gray-500 uppercase">Latency</span>
                        <span className="text-sm text-white">{timeLeft}s</span>
                    </div>
                    <div className="flex flex-col items-end">
                        <span className="text-[9px] text-gray-500 uppercase">Data_Purged</span>
                        <span className="text-sm text-emerald-500">{score.toString().padStart(5, '0')}</span>
                    </div>
                </div>
            </div>

            <div ref={containerRef} className="flex-1 border border-white/5 rounded-xl relative overflow-hidden bg-black/40 cursor-crosshair">
                <AnimatePresence>
                    {gameState === 'idle' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex flex-col items-center justify-center gap-6"
                        >
                            <div className="w-16 h-16 rounded-full border border-emerald-500/30 flex items-center justify-center animate-pulse">
                                <Crosshair className="text-emerald-500 w-8 h-8" />
                            </div>
                            <button
                                onClick={startGame}
                                className="px-10 py-4 border border-emerald-500/50 text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em] hover:bg-emerald-500 hover:text-black transition-all"
                            >
                                Deploy Security Protocol
                            </button>
                        </motion.div>
                    )}

                    {gameState === 'active' && bugs.map(bug => (
                        <motion.button
                            key={bug.id}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 1.5, opacity: 0 }}
                            onClick={() => handleCatch(bug.id)}
                            className="absolute group"
                            style={{ left: `${bug.x}%`, top: `${bug.y}%` }}
                        >
                            <div className="relative">
                                <Bug className="text-red-500/70 group-hover:text-red-400 w-8 h-8" />
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] font-mono text-red-500/50 bg-red-500/10 px-1 py-0.5 rounded">
                                    {bug.type}
                                </div>
                                <motion.div
                                    animate={{ scale: [1, 1.5, 1], opacity: [0.2, 0.5, 0.2] }}
                                    transition={{ duration: 1, repeat: Infinity }}
                                    className="absolute inset-0 bg-red-500 rounded-full blur-xl"
                                />
                            </div>
                        </motion.button>
                    ))}

                    {gameState === 'over' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="absolute inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center text-center p-8 space-y-8"
                        >
                            <ShieldCheck className="text-emerald-500 w-16 h-16" />
                            <div className="space-y-2">
                                <h3 className="text-2xl font-bold text-white uppercase tracking-tighter">System Stabilized</h3>
                                <p className="text-gray-400 font-mono text-[10px] uppercase">Threats neutralized: {score / 100}</p>
                            </div>
                            <button
                                onClick={startGame}
                                className="px-8 py-3 bg-emerald-500 text-black text-xs font-black uppercase tracking-widest rounded shadow-xl"
                            >
                                Restart Scan
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <div className="mt-6 flex justify-between items-center opacity-40">
                <div className="flex gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[8px] font-mono text-white">FIREWALL_ENABLED</span>
                </div>
                <span className="text-[8px] font-mono text-white uppercase tracking-widest">v2.4.0_STABLE</span>
            </div>
        </GlassCard>
    );
}
